import { Image } from 'expo-image';
import random from 'lodash/random';
import { useEffect } from 'react';
import Animated, {
  useAnimatedProps,
  useDerivedValue,
  useSharedValue,
  withDelay,
  withTiming,
} from 'react-native-reanimated';
import { Circle, Svg } from 'react-native-svg';

import { Button } from '@oui/app-core/src/components/Button';
import { Heading, Label, Small, Text } from '@oui/app-core/src/components/Text';
import { View } from '@oui/app-core/src/components/View';
import { useI18n } from '@oui/app-core/src/lib/i18n';
import { Shadow, useTheme } from '@oui/app-core/src/styles';
import { FragmentOf, graphql, readFragment, ResultOf } from '@oui/lib/src/graphql/tada';

import ClockBg from './clock.png';

const ENCOURAGEMENTS = [
  `Nice work; you're moving forward steadily. Try to beat your time next round!`,
  `Keep going; you're making good progress. See if you can improve your pace next time!`,
  `You're doing well and staying on track. Challenge yourself to finish even quicker in the next round!`,
  `Good job; your progress is noticeable. Push to improve your speed a bit next time!`,
  `You're putting in solid effort; it’s paying off. Try to shave off a few seconds in the next round!`,
  `Steady work; each step is getting you closer. Aim for an even faster time next round!`,
  `You're making progress; keep it up. See if you can go a little quicker next time!`,
  `Nice job keeping things on course. Try to set a new personal best in the next round!`,
  `You're advancing well; stick with it. Challenge yourself to improve your time next round!`,
  `You're on the right path; keep up the good work. Go for a faster finish in the next round!`,
];

export const WordPairingResultFragment = graphql(`
  fragment WordPairingResult on WordPairingRound {
    numAnswers
    numCorrectAnswers
    time
  }
`);

const AnimatedCircle = Animated.createAnimatedComponent(Circle);

const STEP_SIZE = 100;
const STROKE_WIDTH = 10;
const RADIUS = (STEP_SIZE - STROKE_WIDTH) / 2;
const CIRCUM = RADIUS * 2 * Math.PI;

const HTML_RADIUS = 228.5;
const PROGRESS_CAP_RADIUS = 40;

function Clock({ data }: { data: ResultOf<typeof WordPairingResultFragment> }) {
  const { theme } = useTheme();
  const { formatNumber } = useI18n();
  const timeSeconds = Math.floor(data.time / 1000);
  const minutes = Math.floor(timeSeconds / 60);
  const seconds = timeSeconds % 60;

  const timerOpacity = useSharedValue(0);
  const percentOpacity = useSharedValue(0);

  const progress = data.numCorrectAnswers / data.numAnswers;
  const animatedProgress = useSharedValue(0);
  const svgProgress = useDerivedValue(() => 100 - animatedProgress.get() * 100);
  const percentCircleProgress = progress;

  useEffect(() => {
    timerOpacity.set(withDelay(100, withTiming(1, { duration: 500 })));

    animatedProgress.set(
      withDelay(
        100 + 500 + 100,
        withTiming(progress, {
          duration: 500,
        }),
      ),
    );

    percentOpacity.set(withDelay(100 + 500 + 100 + 500 + 100, withTiming(1, { duration: 500 })));
  }, [timerOpacity, percentOpacity, animatedProgress, progress]);

  const animatedProps = useAnimatedProps(() => {
    const strokeDashoffset = RADIUS * Math.PI * 2 * (svgProgress.get() / 100);
    return {
      strokeDashoffset,
    };
  });

  return (
    <View
      style={{
        borderRadius: 10,
        backgroundColor: 'white',
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 50,
        paddingVertical: 20,
      }}
    >
      <Image source={ClockBg} style={{ width: 242, height: 277 }} />
      <Animated.View
        style={{
          position: 'absolute',
          top: '50%',
          transform: [{ translateY: -35 }],
          opacity: timerOpacity,
        }}
      >
        <Text text="Time" size={17} textAlign="center" />
        <Text
          size={theme.typography.heading1.size}
          weight={theme.typography.heading1.weight}
          lineHeight={theme.typography.heading1.lineHeight}
          text={`${minutes}:${seconds.toString().padStart(2, '0')}`}
          textAlign="center"
          color={theme.color.gray100}
          style={{
            letterSpacing: 5,
            // actual centered text looks visually off-center, so we adjust
            marginLeft: 5,
          }}
        />
        <Animated.View style={{ opacity: percentOpacity }}>
          <Text
            text={`${data.numCorrectAnswers}/${data.numAnswers} Correct`}
            size={17}
            textAlign="center"
            style={{ marginTop: 20 }}
          />
        </Animated.View>
      </Animated.View>
      <View
        style={{
          position: 'absolute',
          top: 63,
          transform: [{ translateX: 2 }],
          width: HTML_RADIUS,
          height: HTML_RADIUS,
          alignSelf: 'center',
        }}
      >
        <Svg viewBox={`0 0 ${STEP_SIZE} ${STEP_SIZE}`}>
          <AnimatedCircle
            stroke={theme.color.accent100}
            fill="transparent"
            cx={STEP_SIZE / 2}
            cy={STEP_SIZE / 2}
            r={RADIUS}
            strokeDasharray={`${CIRCUM} ${CIRCUM}`}
            animatedProps={animatedProps}
            strokeLinecap="round"
            transform={`rotate(-90, ${STEP_SIZE / 2}, ${STEP_SIZE / 2})`}
            strokeWidth={`${STROKE_WIDTH}`}
          />
        </Svg>
        <Animated.View
          style={[
            {
              opacity: percentOpacity,
              width: PROGRESS_CAP_RADIUS,
              height: PROGRESS_CAP_RADIUS,
              borderRadius: PROGRESS_CAP_RADIUS / 2,
              borderWidth: 1,
              borderColor: theme.color.gray700,
              backgroundColor: 'white',
              alignItems: 'center',
              justifyContent: 'center',
              // position & first two transforms are meant to center element in circle
              top: '50%',
              left: '50%',
              transform: [
                { translateX: -PROGRESS_CAP_RADIUS / 2 },
                { translateY: -PROGRESS_CAP_RADIUS / 2 },
                // remaining transforms position on the arc of the outer edge of the circle
                {
                  translateX:
                    ((HTML_RADIUS - STROKE_WIDTH * 2) / 2) *
                    Math.sin(percentCircleProgress * 2 * Math.PI),
                },
                {
                  translateY:
                    (-(HTML_RADIUS - STROKE_WIDTH * 2) / 2) *
                    Math.cos(percentCircleProgress * 2 * Math.PI),
                },
              ],
              position: 'absolute',
            },
            Shadow.low,
          ]}
        >
          <Small text={formatNumber(progress, { style: 'percent' })} />
        </Animated.View>
      </View>
    </View>
  );
}

/**
 * Shows results for a single round.
 */
export const WordPairingResult = ({
  round,
  data: _data,
  onNext,
}: {
  round: number;
  data: FragmentOf<typeof WordPairingResultFragment>;
  onNext: () => unknown;
}) => {
  const data = readFragment(WordPairingResultFragment, _data);
  return (
    <View testID="WordPairingResult" style={{ gap: 50 }}>
      <View style={{ gap: 20 }}>
        <Heading level={2} text={`Round ${round + 1}`} textAlign="center" color="white" />
        <Clock data={data} />
      </View>
      <View style={{ gap: 25 }}>
        <Label
          text={
            ENCOURAGEMENTS[
              process.env.STORYBOOK_CLIENT ? 0 : random(0, ENCOURAGEMENTS.length - 1, false)
            ]
          }
          textAlign="center"
          color="white"
        />
        <Button
          text="Next"
          iconRight="arrow-right"
          onPress={onNext}
          variant="solid"
          _accentColor="white"
        />
      </View>
    </View>
  );
};
